import { useCallback, useState, useEffect } from "react";
import { LoadedData, useLoadedData } from "../../../hooks/useLoadedData";

export interface TextSearchPanelData<T> {
    filter: string;
    setFilter: (val: string) => void;
    clearFilter: () => void;
    results: LoadedData<T[]>;
    isSearching: boolean;
    isActive: boolean;
    setIsActive: (active: boolean) => void;
}

export function useTextSearchPanel<T>(apiPath: string, queryParam: string = "search"): TextSearchPanelData<T> {
    const [isActive, setIsActive] = useState<boolean>(false);
    
    const [filter, setFilter] = useState("");
    const [debouncedFilter, setDebouncedFilter] = useState("");

    // Debounce logic (500ms)
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedFilter(filter);
        }, 500);

        return () => {
            clearTimeout(handler);
        };
    }, [filter]);

    const clearFilter = useCallback(() => {
        setFilter("");
        setDebouncedFilter(""); // Also clear debounced to avoid brief flicker on re-open
    }, []);

    // Side-effect: When the panel is closed, reset everything
    useEffect(() => {
        if (!isActive) {
            clearFilter();
        }
    }, [isActive, clearFilter]);

    // Construct the URL. Only search if there is a debounced filter AND panel is active.
    // (We add `isActive` to ensure background searches don't trigger while closed)
    const searchUrl = isActive && debouncedFilter.trim() 
        ? `${apiPath}?${queryParam}=${encodeURIComponent(debouncedFilter.trim())}` 
        : "";

    const results = useLoadedData<T[]>(searchUrl, [], !!searchUrl);

    const isSearching = filter.trim().length > 0 && (results.isLoading || filter !== debouncedFilter);

    return {
        filter,
        setFilter,
        clearFilter,
        results,
        isSearching,
        isActive,
        setIsActive,
    };
}